import { Arena, OWNER_WORDS, blockCapacity } from "./arena.ts";
import { Errno, WasiError } from "./errno.ts";
import { components } from "./path.ts";
import {
    Filetype,
    type DirEntry,
    type Handle,
    type OpenOptions,
    type Stat,
    type Vfs,
    type VfsSnapshot,
} from "./vfs.ts";

/**
 * In-memory Vfs implementation stored within a shared Arena buffer.
 *
 * Inodes, directory entries, and file content blocks are stored at byte offsets
 * inside a shared Arena, allowing multiple worker processes to access the same
 * filesystem via SharedArrayBuffer. File handles correspond directly to inode offsets.
 */

const Kind = { FILE: 1, DIR: 2, SYMLINK: 3 } as const;

// Inode fields, as byte offsets from the inode.
/** The kind in the low byte, and above it the generation the inode was last marked in. */
const I_KIND = 0;
const I_SIZE = 4;
const I_DATA = 8; // offset of the data block, 0 if none
const I_CAP = 12; // usable bytes in that block
const I_OPENS = 16;
const I_INO = 20;
const I_ENTRIES = 24; // first directory entry, 0 if empty
/** The containing directory. Zero once unlinked; the root points at itself. */
const I_PARENT = 28;
const I_ATIME = 32; // three doubles, so from here everything is 8-aligned
const I_MTIME = 40;
const I_CTIME = 48;
const INODE_BYTES = 56;

// Directory entry fields. The name follows the header, unpadded.
const D_NEXT = 0;
const D_NODE = 4;
const D_NAMELEN = 8;
const D_NAME = 12;

// Change marks, as arena header words.
const M_GENERATION = OWNER_WORDS; // what a change marks an inode with now
const M_MARKED = OWNER_WORDS + 1; // nonzero once anything is marked in this generation
const GENERATION_MASK = 0xffffff;

/** Where `takeChanges` looks: a directory and everything below it, or one entry and its own entries. */
export interface ChangeRoot {
    path: string;
    recursive: boolean;
}

/**
 * What changed since the last take. A directory is listed when an entry in it
 * or below it changed, so a caller compares the listing with the one it has.
 */
export interface MarkedChanges {
    files: string[];
    directories: { path: string; entries: DirEntry[] }[];
}

/** Device identifier constant. */
const DEV = 1;

/** Maximum symlink depth during resolution before reporting ELOOP. */
const SYMLINK_BUDGET = 40;

const encoder = new TextEncoder();
const decoder = new TextDecoder();

export interface MemVfsOptions {
    /** Bytes reserved up front. It grows on demand. */
    initialBytes?: number;
    /** The most it may grow to. A write past this is ENOSPC. */
    maxBytes?: number;
}

export class MemVfs implements Vfs {
    readonly #arena: Arena;

    private constructor(arena: Arena) {
        this.#arena = arena;
    }

    static create(opts: MemVfsOptions = {}): MemVfs {
        const arena = Arena.create(opts.initialBytes ?? 1 << 20, opts.maxBytes ?? 1 << 30);
        const fs = new MemVfs(arena);
        arena.locked(() => {
            const root = fs.#newInode(Kind.DIR);
            arena.root = root;
            fs.#set(root + I_PARENT, root);
            arena.words[M_GENERATION] = 1;
        });
        return fs;
    }

    /** Attaches to an existing shared memory arena. */
    static attach(snapshot: { arena: SharedArrayBuffer }): MemVfs {
        return new MemVfs(Arena.attach(snapshot.arena));
    }

    get id(): string {
        return `mem:${this.#arena.id}`;
    }

    /** Memory usage in bytes (allocated bytes used and total reserved buffer length). */
    get usage(): { used: number; reserved: number } {
        return { used: this.#arena.used, reserved: this.#arena.sab.byteLength };
    }

    snapshot(): VfsSnapshot {
        return { kind: "mem", arena: this.#arena.sab };
    }

    // ---- the interface -----------------------------------------------------

    open(path: string, opts: OpenOptions): Handle {
        return this.#arena.locked(() => {
            const { parent, name, node } = this.#resolve(path, true);
            let inode = node;
            if (inode === 0) {
                if (!opts.create) throw new WasiError(Errno.NOENT);
                inode = this.#link(parent, name, this.#newInode(Kind.FILE));
                this.#mark(inode);
            } else {
                if (opts.create && opts.exclusive) throw new WasiError(Errno.EXIST);
                if (this.#kind(inode) === Kind.DIR) throw new WasiError(Errno.ISDIR);
                if (opts.truncate) {
                    this.#resize(inode, 0);
                    this.#mark(inode);
                }
            }
            this.#set(inode + I_OPENS, this.#get(inode + I_OPENS) + 1);
            return inode;
        });
    }

    processEnded(): void {}

    retain(handle: Handle): void {
        this.#arena.locked(() => {
            this.#set(handle + I_OPENS, this.#get(handle + I_OPENS) + 1);
        });
    }

    close(handle: Handle): void {
        this.#arena.locked(() => {
            const opens = this.#get(handle + I_OPENS) - 1;
            this.#set(handle + I_OPENS, opens);
            // Release inode if unlinked and all open handles are closed.
            if (opens <= 0 && this.#get(handle + I_PARENT) === 0) this.#destroy(handle);
        });
    }

    read(handle: Handle, into: Uint8Array, position: number): number {
        return this.#arena.locked(() => {
            const size = this.#get(handle + I_SIZE);
            const n = Math.max(0, Math.min(into.length, size - position));
            if (n === 0) return 0;
            const data = this.#get(handle + I_DATA);
            into.set(this.#arena.bytes.subarray(data + position, data + position + n));
            return n;
        });
    }

    write(handle: Handle, from: Uint8Array, position: number | null): number {
        return this.#arena.locked(() => {
            const size = this.#get(handle + I_SIZE);
            // An append is only atomic because the whole call is: nothing else
            // can get between the read of the size and the write.
            const at = position ?? size;
            if (from.length > 0) {
                this.#reserve(handle, at + from.length);
                this.#arena.bytes.set(from, this.#get(handle + I_DATA) + at);
                if (at + from.length > size) this.#set(handle + I_SIZE, at + from.length);
                this.#touch(handle, true);
                this.#mark(handle);
            }
            return from.length;
        });
    }

    truncate(handle: Handle, size: number): void {
        this.#arena.locked(() => {
            this.#resize(handle, size);
            this.#touch(handle, true);
            this.#mark(handle);
        });
    }

    stat(path: string, follow: boolean): Stat {
        return this.#arena.locked(() => this.#stat(this.#existing(path, follow)));
    }

    statHandle(handle: Handle): Stat {
        return this.#arena.locked(() => this.#stat(handle));
    }

    setTimes(path: string, atimeMs: number, mtimeMs: number, follow: boolean): void {
        this.#arena.locked(() => {
            const inode = this.#existing(path, follow);
            this.#setTimes(inode, atimeMs, mtimeMs);
            this.#mark(inode);
        });
    }

    setTimesHandle(handle: Handle, atimeMs: number, mtimeMs: number): void {
        this.#arena.locked(() => {
            this.#setTimes(handle, atimeMs, mtimeMs);
            this.#mark(handle);
        });
    }

    rename(from: string, to: string): void {
        this.#arena.locked(() => {
            const source = this.#resolve(from, false);
            const target = this.#resolve(to, false);
            if (source.node === 0) throw new WasiError(Errno.NOENT);
            if (source.name === "" || target.name === "") throw new WasiError(Errno.INVAL);
            if (source.node === target.node) return; // the same file under the same name
            const isDir = this.#kind(source.node) === Kind.DIR;

            // Prevent moving a directory into its own descendant tree.
            if (isDir) {
                for (let up = target.parent; ;) {
                    if (up === source.node) throw new WasiError(Errno.INVAL);
                    const next = this.#get(up + I_PARENT);
                    if (next === up || next === 0) break;
                    up = next;
                }
            }
            if (target.node !== 0) {
                const targetIsDir = this.#kind(target.node) === Kind.DIR;
                if (isDir && !targetIsDir) throw new WasiError(Errno.NOTDIR);
                if (!isDir && targetIsDir) throw new WasiError(Errno.ISDIR);
                if (targetIsDir && this.#get(target.node + I_ENTRIES) !== 0) {
                    throw new WasiError(Errno.NOTEMPTY);
                }
                this.#unlinkNode(target.parent, target.name);
            }
            this.#mark(source.parent);
            this.#detach(source.parent, source.name);
            this.#link(target.parent, target.name, source.node);
            this.#touch(source.node, false);
            this.#mark(source.node);
        });
    }

    unlink(path: string): void {
        this.#arena.locked(() => {
            const { parent, name, node } = this.#resolve(path, false);
            if (node === 0) throw new WasiError(Errno.NOENT);
            if (this.#kind(node) === Kind.DIR) throw new WasiError(Errno.ISDIR);
            this.#unlinkNode(parent, name);
            this.#mark(parent);
        });
    }

    mkdir(path: string): void {
        this.#arena.locked(() => {
            const { parent, name, node } = this.#resolve(path, false);
            if (node !== 0) throw new WasiError(Errno.EXIST);
            this.#mark(this.#link(parent, name, this.#newInode(Kind.DIR)));
        });
    }

    rmdir(path: string): void {
        this.#arena.locked(() => {
            const { parent, name, node } = this.#resolve(path, false);
            if (node === 0) throw new WasiError(Errno.NOENT);
            if (this.#kind(node) !== Kind.DIR) throw new WasiError(Errno.NOTDIR);
            if (node === this.#arena.root) throw new WasiError(Errno.INVAL);
            if (this.#get(node + I_ENTRIES) !== 0) throw new WasiError(Errno.NOTEMPTY);
            this.#unlinkNode(parent, name);
            this.#mark(parent);
        });
    }

    readlink(path: string): string {
        return this.#arena.locked(() => {
            const node = this.#existing(path, false);
            if (this.#kind(node) !== Kind.SYMLINK) throw new WasiError(Errno.INVAL);
            return this.#contents(node);
        });
    }

    readdir(path: string): DirEntry[] {
        return this.#arena.locked(() => {
            const node = this.#existing(path, true);
            if (this.#kind(node) !== Kind.DIR) throw new WasiError(Errno.NOTDIR);
            const out: DirEntry[] = [];
            for (let e = this.#get(node + I_ENTRIES); e !== 0; e = this.#get(e + D_NEXT)) {
                const target = this.#get(e + D_NODE);
                out.push({
                    name: this.#entryName(e),
                    ino: this.#get(target + I_INO),
                    filetype: filetypeOf(this.#kind(target)),
                });
            }
            return out;
        });
    }

    // ---- beyond the interface ----------------------------------------------

    /** Creates a symbolic link in the in-memory filesystem. */
    symlink(target: string, path: string): void {
        this.#arena.locked(() => {
            const { parent, name, node } = this.#resolve(path, false);
            if (node !== 0) throw new WasiError(Errno.EXIST);
            const link = this.#newInode(Kind.SYMLINK);
            const bytes = encoder.encode(target);
            this.#reserve(link, bytes.length);
            this.#arena.bytes.set(bytes, this.#get(link + I_DATA));
            this.#set(link + I_SIZE, bytes.length);
            this.#mark(this.#link(parent, name, link));
        });
    }

    /**
     * What changed under the roots since the last take, in every MemVfs
     * attached to this arena. It may name entries that did not change, but not
     * leave out one that did. One caller takes: a take starts a generation.
     */
    takeChanges(roots: readonly ChangeRoot[]): MarkedChanges {
        return this.#arena.locked(() => {
            const changes: MarkedChanges = { files: [], directories: [] };
            if (this.#arena.words[M_MARKED] === 0) return changes;
            const taken = this.#arena.words[M_GENERATION]!;
            this.#arena.words[M_MARKED] = 0;
            this.#arena.words[M_GENERATION] = (taken + 1) & GENERATION_MASK || 1;
            const seen = new Set<string>();
            const visit = (inode: number, path: string, recursive: boolean, top: boolean) => {
                if (this.#generation(inode) !== taken || seen.has(path)) return;
                seen.add(path);
                if (this.#kind(inode) !== Kind.DIR) {
                    changes.files.push(path);
                    return;
                }
                if (!recursive && !top) return;
                const entries: DirEntry[] = [];
                for (let e = this.#get(inode + I_ENTRIES); e !== 0; e = this.#get(e + D_NEXT)) {
                    const child = this.#get(e + D_NODE);
                    const name = this.#entryName(e);
                    entries.push({ name, ino: this.#get(child + I_INO), filetype: filetypeOf(this.#kind(child)) });
                    visit(child, path === "/" ? `/${name}` : `${path}/${name}`, recursive, false);
                }
                changes.directories.push({ path, entries });
            };
            // Recursive roots first, so that a directory seen from a shallow
            // root is not skipped when a recursive one reaches it.
            const ordered = [...roots].sort((a, b) => Number(b.recursive) - Number(a.recursive));
            for (const root of ordered) {
                let node = 0;
                try {
                    node = this.#resolve(root.path, true).node;
                } catch {
                    // Not there: nothing under it to report.
                }
                if (node !== 0) visit(node, root.path, root.recursive, true);
            }
            return changes;
        });
    }

    // ---- inside the lock ---------------------------------------------------

    #get(offset: number): number {
        return this.#arena.words[offset >> 2]!;
    }

    #set(offset: number, value: number): void {
        this.#arena.words[offset >> 2] = value;
    }

    #time(offset: number): number {
        return this.#arena.doubles[offset >> 3]!;
    }

    #setTime(offset: number, value: number): void {
        this.#arena.doubles[offset >> 3] = value;
    }

    #kind(inode: number): number {
        return this.#get(inode + I_KIND) & 0xff;
    }

    #generation(inode: number): number {
        return this.#get(inode + I_KIND) >>> 8;
    }

    /**
     * Mark an inode and its ancestors as changed in this generation. The walk
     * stops at the first one already marked, since everything above it is too.
     */
    #mark(inode: number): void {
        const generation = this.#arena.words[M_GENERATION]!;
        for (let node = inode; ;) {
            const word = this.#get(node + I_KIND);
            if (word >>> 8 === generation) return;
            this.#set(node + I_KIND, (word & 0xff) | (generation << 8));
            this.#arena.words[M_MARKED] = 1;
            const up = this.#get(node + I_PARENT);
            if (up === 0 || up === node) return;
            node = up;
        }
    }

    #newInode(kind: number): number {
        const inode = this.#arena.alloc(INODE_BYTES);
        const now = Date.now();
        this.#set(inode + I_KIND, kind);
        this.#set(inode + I_INO, this.#arena.nextIno());
        this.#setTime(inode + I_ATIME, now);
        this.#setTime(inode + I_MTIME, now);
        this.#setTime(inode + I_CTIME, now);
        return inode;
    }

    /** Adds a directory entry pointing to inode and updates parent pointer. */
    #link(dir: number, name: string, inode: number): number {
        if (name === "") throw new WasiError(Errno.INVAL);
        const bytes = encoder.encode(name);
        const entry = this.#arena.alloc(D_NAME + bytes.length);
        this.#set(entry + D_NEXT, this.#get(dir + I_ENTRIES));
        this.#set(entry + D_NODE, inode);
        this.#set(entry + D_NAMELEN, bytes.length);
        this.#arena.bytes.set(bytes, entry + D_NAME);
        this.#set(dir + I_ENTRIES, entry);
        this.#set(inode + I_PARENT, dir);
        this.#touch(dir, true);
        return inode;
    }

    /** Removes a name entry from directory, returning the target inode. */
    #detach(dir: number, name: string): number {
        let previous = 0;
        for (let entry = this.#get(dir + I_ENTRIES); entry !== 0;) {
            const next = this.#get(entry + D_NEXT);
            if (this.#entryName(entry) === name) {
                if (previous === 0) this.#set(dir + I_ENTRIES, next);
                else this.#set(previous + D_NEXT, next);
                const inode = this.#get(entry + D_NODE);
                this.#arena.free(entry);
                this.#touch(dir, true);
                return inode;
            }
            previous = entry;
            entry = next;
        }
        throw new WasiError(Errno.NOENT);
    }

    /** Removes a directory entry and frees inode if not currently open. */
    #unlinkNode(dir: number, name: string): void {
        const inode = this.#detach(dir, name);
        this.#set(inode + I_PARENT, 0);
        if (this.#get(inode + I_OPENS) === 0) this.#destroy(inode);
    }

    #destroy(inode: number): void {
        this.#arena.free(this.#get(inode + I_DATA));
        this.#arena.free(inode);
    }

    #lookup(dir: number, name: string): number {
        if (this.#kind(dir) !== Kind.DIR) throw new WasiError(Errno.NOTDIR);
        // Compare ASCII names directly without full UTF-8 encode step.
        const wanted = isAscii(name) ? name : encoder.encode(name);
        for (let entry = this.#get(dir + I_ENTRIES); entry !== 0; entry = this.#get(entry + D_NEXT)) {
            if (this.#entryNamed(entry, wanted)) return this.#get(entry + D_NODE);
        }
        return 0;
    }

    #entryNamed(entry: number, wanted: string | Uint8Array): boolean {
        if (this.#get(entry + D_NAMELEN) !== wanted.length) return false;
        const bytes = this.#arena.bytes;
        for (let i = 0; i < wanted.length; i++) {
            const c = typeof wanted === "string" ? wanted.charCodeAt(i) : wanted[i];
            if (bytes[entry + D_NAME + i] !== c) return false;
        }
        return true;
    }

    #entryName(entry: number): string {
        const length = this.#get(entry + D_NAMELEN);
        return decoder.decode(this.#arena.bytes.slice(entry + D_NAME, entry + D_NAME + length));
    }

    /**
     * Resolves a path to its parent directory inode, final entry name, and target inode.
     * Intermediate symlinks are followed; the final component is followed only if followFinal is true.
     */
    #resolve(path: string, followFinal: boolean): { parent: number; name: string; node: number } {
        if (!path.startsWith("/")) throw new WasiError(Errno.INVAL);
        let dir = this.#arena.root;
        let rest = components(path);
        let budget = SYMLINK_BUDGET;

        for (;;) {
            const part = rest[0];
            if (part === undefined) {
                // Path resolved directly to a directory with no trailing component.
                return { parent: this.#get(dir + I_PARENT), name: "", node: dir };
            }
            const tail = rest.slice(1);
            if (part === "..") {
                dir = this.#get(dir + I_PARENT);
                rest = tail;
                continue;
            }

            const node = this.#lookup(dir, part);
            const last = tail.length === 0;
            if (node === 0) {
                if (!last) throw new WasiError(Errno.NOENT);
                return { parent: dir, name: part, node: 0 };
            }
            if (this.#kind(node) === Kind.SYMLINK && (!last || followFinal)) {
                if (budget-- === 0) throw new WasiError(Errno.LOOP);
                const target = this.#contents(node);
                if (target.startsWith("/")) dir = this.#arena.root;
                rest = [...components(target), ...tail];
                continue;
            }
            if (last) return { parent: dir, name: part, node };
            dir = node;
            rest = tail;
        }
    }

    /** Resolves path and returns its inode, or throws ENOENT. */
    #existing(path: string, follow: boolean): number {
        const { node } = this.#resolve(path, follow);
        if (node === 0) throw new WasiError(Errno.NOENT);
        return node;
    }

    /** A file's or a symlink's bytes as a string. */
    #contents(inode: number): string {
        const data = this.#get(inode + I_DATA);
        return decoder.decode(this.#arena.bytes.slice(data, data + this.#get(inode + I_SIZE)));
    }

    /**
     * Allocates or expands capacity for file data, maintaining zero-padding invariant.
     * The capacity is all its block holds, so doubling it moves up one size class.
     */
    #reserve(inode: number, bytes: number): void {
        const capacity = this.#get(inode + I_CAP);
        if (bytes <= capacity) return;
        const wanted = blockCapacity(Math.max(bytes, capacity * 2, 64));
        const block = this.#arena.alloc(wanted);
        const old = this.#get(inode + I_DATA);
        if (old !== 0) {
            const size = this.#get(inode + I_SIZE);
            this.#arena.bytes.copyWithin(block, old, old + size);
            this.#arena.free(old);
        }
        this.#set(inode + I_DATA, block);
        this.#set(inode + I_CAP, wanted);
    }

    #resize(inode: number, size: number): void {
        const old = this.#get(inode + I_SIZE);
        if (size > old) this.#reserve(inode, size);
        else if (size < old) {
            const data = this.#get(inode + I_DATA);
            this.#arena.bytes.fill(0, data + size, data + old);
        }
        this.#set(inode + I_SIZE, size);
    }

    #stat(inode: number): Stat {
        const kind = this.#kind(inode);
        return {
            filetype: filetypeOf(kind),
            dev: DEV,
            ino: this.#get(inode + I_INO),
            nlink: kind === Kind.DIR ? 2 : 1,
            size: this.#get(inode + I_SIZE),
            atimeMs: this.#time(inode + I_ATIME),
            mtimeMs: this.#time(inode + I_MTIME),
            ctimeMs: this.#time(inode + I_CTIME),
        };
    }

    #setTimes(inode: number, atimeMs: number, mtimeMs: number): void {
        this.#setTime(inode + I_ATIME, atimeMs);
        this.#setTime(inode + I_MTIME, mtimeMs);
        this.#setTime(inode + I_CTIME, Date.now());
    }

    /** Updates mtime and ctime timestamps. */
    #touch(inode: number, modified: boolean): void {
        const now = Date.now();
        if (modified) this.#setTime(inode + I_MTIME, now);
        this.#setTime(inode + I_CTIME, now);
    }
}

function isAscii(s: string): boolean {
    for (let i = 0; i < s.length; i++) {
        if (s.charCodeAt(i) > 127) return false;
    }
    return true;
}

function filetypeOf(kind: number): Filetype {
    return kind === Kind.DIR
        ? Filetype.DIRECTORY
        : kind === Kind.SYMLINK
          ? Filetype.SYMBOLIC_LINK
          : Filetype.REGULAR_FILE;
}
